import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'

interface CookiesStatus {
  hasCookies: boolean
  cookieCount: number
}

interface SaveCookiesResponse {
  ok: boolean
  cookieCount: number
}

interface RunResponse {
  ok: boolean
  screenshot: string
  log: string
}

export interface ScriptStep {
  type: 'scroll' | 'click' | 'wait'
  count?: number
  delay?: number
  selector?: string
  label?: string
  seconds?: number
}

export interface Script {
  id: string
  name: string
  url: string
  steps: ScriptStep[]
  created_at: string
  updated_at: string
}

export function useCheckerCookies() {
  return useQuery({
    queryKey: ['checker-bot-cookies'],
    queryFn: () => api.get<CookiesStatus>('/checker-bot/cookies'),
  })
}

export function useSaveCookies() {
  return useMutation({
    mutationFn: (cookies: string) =>
      api.post<SaveCookiesResponse>('/checker-bot/cookies', { cookies }),
  })
}

export function useRunChecker() {
  return useMutation({
    mutationFn: (url: string) =>
      api.post<RunResponse>('/checker-bot/run', { url }),
  })
}

export function useScripts() {
  return useQuery({
    queryKey: ['checker-bot-scripts'],
    queryFn: () => api.get<Script[]>('/checker-bot/scripts'),
  })
}

export function useCreateScript() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; url: string; steps: ScriptStep[] }) =>
      api.post<Script>('/checker-bot/scripts', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['checker-bot-scripts'] }),
  })
}

export function useUpdateScript() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; name: string; url: string; steps: ScriptStep[] }) =>
      api.put<Script>(`/checker-bot/scripts/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['checker-bot-scripts'] }),
  })
}

export function useDeleteScript() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ ok: boolean }>(`/checker-bot/scripts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['checker-bot-scripts'] }),
  })
}
